// Phone "Today" screen (phones + installed app only; desktop keeps the full Dashboard).
// Uses the same tables the dashboard reads plus the to-do snags and the port weather - no AI call on load.
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { fetchRows } from '../lib/api'
import { supabase } from '../lib/supabase'
import { getShipTz, shipTodayAuto } from '../lib/shipTime'
import { expiryStatus } from '../lib/expiry'
import { setHandoff } from '../lib/phoneHandoff'
import ApprovalsCard, { rememberOpen } from '../components/ApprovalsCard'
import { ISO_DATETIME } from '../lib/tableText'
import VesselStatusTiles from '../components/VesselStatusTiles'
import GettingStarted from '../components/GettingStarted'
import { buildTiles } from '../lib/dashboardCore'
import { isSimple, simpleTiles } from '../service/simpleMode'
import VesselJobsCard from '../service/VesselJobsCard'
import { DRILL_TYPES, drillsNeedingAttention } from '../lib/drillTypes'

const DAY = 86400000
function getVal(v) {
  if (!v) return ''
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'object' && !Array.isArray(v)) return v.value || v.name || v.label || ''
  if (Array.isArray(v)) return v.length ? (v[0].value || v[0].name || String(v[0])) : ''
  const s = String(v)
  return ISO_DATETIME.test(s) ? s.slice(0, 10) : s   // only a date-time is cut (not "Thruster ...")
}
const status = r => getVal(r['Status'])
// date-only strings at local noon so the timezone can't shift the day
function dayOf(s) {
  s = String(s || '').trim()
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s), dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s)
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3], 12).getTime()
  if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1], 12).getTime()
  const t = Date.parse(s); return t || null
}

const PERM = { chat: 'ai', checklists: 'checklists', hours_rest: 'hours_rest', todo: 'todo' }
// module -> feature key, as the Dashboard (its "see")
const FEATURE = { maintenance: 'mtask', documents: 'docs', risk: 'risk_assessments', ptw: 'risk_assessments' }

export default function Today({ onNavigate }) {
  const { config, can, hasFeature } = useAuth()
  const [d, setD] = useState(null)
  const [wx, setWx] = useState(null)
  const photoRef = useRef(null)
  const allowed = id => (!can || can(PERM[id] || id, 'read')) && (!hasFeature || hasFeature(id))

  useEffect(() => {
    if (!config?.id) return
    let live = true
    ;(async () => {
      const [eq, tasks, docs, crewDocs, cal, charters, portCalls, inv] = await Promise.all(
        ['equipment', 'mtask', 'docs', 'crew_docs', 'calendar', 'charters', 'port_calls', 'inventory'].map(t => fetchRows(t).catch(() => [])))
      // the drill records for the Drills tile (the same read as the Dashboard)
      let drills = []
      try { const { data } = await supabase.from('drill_records').select('drill_type, drill_date').eq('client_id', config.id); drills = data || [] } catch (e) {}
      let todos = []
      try {
        const { data: ls } = await supabase.from('todo_lists').select('id').eq('client_id', config.id)
        const ids = (ls || []).map(l => l.id)
        if (ids.length) { const { data } = await supabase.from('todo_items').select('id,text,priority,due_date,done').in('list_id', ids).eq('done', false); todos = data || [] }
      } catch (e) {}
      if (live) setD({ eq: eq || [], tasks: tasks || [], docs: docs || [], crewDocs: crewDocs || [], cal: cal || [], charters: charters || [], portCalls: portCalls || [], todos, inv: inv || [], drills })
    })()
    return () => { live = false }
  }, [config?.id])

  // location = port we are in (ETA passed, not yet departed), else the next one
  const today = shipTodayAuto()
  const ports = (d?.portCalls || []).map(r => ({ port: getVal(r['Port']), country: getVal(r['Country']), eta: getVal(r['ETA Date']), etd: getVal(r['ETD Date']) })).filter(p => p.port)
  const inPort = ports.filter(p => p.eta && p.eta <= today && (!p.etd || p.etd >= today)).sort((a, b) => b.eta.localeCompare(a.eta))[0]
  const nextPort = ports.filter(p => p.eta && p.eta > today).sort((a, b) => a.eta.localeCompare(b.eta))[0]
  const place = inPort || nextPort || null
  useEffect(() => {
    if (!place) return
    let live = true
    const q = new URLSearchParams({ port: place.port }); if (place.country) q.set('country', place.country)
    fetch('/api/weather?' + q).then(r => r.json()).then(j => { if (live && !j.error) setWx(j.current || j) }).catch(() => {})
    return () => { live = false }
  }, [place && place.port, place && place.country]) // eslint-disable-line

  const go = (page, filter = '') => onNavigate && onNavigate(page, filter)
  // the Vessel status tiles, as on the Dashboard (same rules, same rights): first on the screen, 2 per row
  const see = m => (!can || can(m, 'read')) && (!hasFeature || hasFeature(FEATURE[m] || m))
  const tiles0 = d ? buildTiles({ eq: d.eq, tasks: d.tasks, docs: d.docs, crewDocs: d.crewDocs, inv: d.inv, drills: drillsNeedingAttention(d.drills), drillTypes: DRILL_TYPES.filter(x => x.freqMonths > 0).length, drillRecords: d.drills.length }, see, today) : []
  const tiles = isSimple(config?.service, config?.id) ? simpleTiles(tiles0) : tiles0   // Service package, simple mode
  function onPhoto(e) {
    const f = e.target.files && e.target.files[0]; e.target.value = ''
    if (!f) return
    setHandoff({ photo: f, text: 'Snag: ' }); go('chat')
  }

  const dateLabel = (() => { try { return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', ...(getShipTz() ? { timeZone: getShipTz() } : {}) }) } catch (e) { return today } })()

  // top 3 for today: high-priority snags due today first, then today's diary
  const tday = dayOf(today)
  const isToday = s => { const t = dayOf(s); return t != null && Math.abs(t - tday) < DAY / 2 }
  const top = []
  if (d) {
    d.todos.filter(t => t.due_date && dayOf(t.due_date) <= tday).sort((a, b) => (b.priority || 0) - (a.priority || 0)).forEach(t => top.push({ label: t.text, tag: dayOf(t.due_date) < tday ? 'overdue' : 'to do', page: 'todo' }))
    d.cal.forEach(r => { if (isToday(getVal(r['Start Date']))) top.push({ label: getVal(r['Title']) || getVal(r['Type']) || 'Event', tag: 'calendar', page: 'calendar' }) })
    d.charters.forEach(r => { if (isToday(getVal(r['Start Date']))) top.push({ label: 'Charter: ' + (getVal(r['Charter Ref']) || getVal(r['Lead Guest']) || getVal(r['Title']) || 'starts'), tag: 'charter', page: 'charters' }) })
    d.portCalls.forEach(r => { if (isToday(getVal(r['ETA Date']))) top.push({ label: 'Arrive ' + getVal(r['Port']), tag: 'port', page: 'port_calls' }) })
    d.tasks.forEach(r => { if (isToday(getVal(r['Next Service Date']))) top.push({ label: getVal(r['Task Name']) || getVal(r['Name']) || 'Maintenance', tag: 'service due', page: 'mtask' }) })
  }

  // alerts: overdue maintenance, out of service, certificates expiring < 30 days, high-priority snags
  const alerts = []
  if (d) {
    d.tasks.filter(t => status(t) === 'Overdue').forEach(t => alerts.push({ rank: 0, tone: 'r', label: (getVal(t['Task Name']) || getVal(t['Name']) || 'Task'), what: 'Overdue', page: 'mtask' }))
    d.eq.filter(e => status(e) === 'Out of Service').forEach(e => alerts.push({ rank: 0, tone: 'r', label: getVal(e['Name']) || 'Equipment', what: 'Out of service', page: 'equipment' }))
    // the shared expiry rule (src/lib/expiry.js): valid through the expiry date (ship's time); here: expired + the next 30 days
    const cert = (r, page) => {
      const s = expiryStatus(getVal(r['Expiry Date']), today); if (s.days == null || s.days > 30) return
      alerts.push({ rank: s.expired ? 0 : 1, tone: s.expired ? 'r' : 'a', label: getVal(r['Name']) || getVal(r['Title']) || 'Certificate', what: s.expired ? 'Expired' : s.days === 0 ? 'Expires today' : 'Expires in ' + s.days + (s.days === 1 ? ' day' : ' days'), page })
    }
    d.docs.filter(r => status(r) !== 'Archived').forEach(r => cert(r, 'docs'))
    if (!(config?.accountType === 'crew' && (config?.permissions || {}).crew === 'none')) d.crewDocs.forEach(r => cert(r, 'crew_certs'))
    d.todos.filter(t => t.priority === 3).forEach(t => alerts.push({ rank: 1, tone: 'a', label: t.text || 'Snag', what: 'High priority snag', page: 'todo' }))
    alerts.sort((a, b) => a.rank - b.rank)
  }

  const QUICK = [
    { id: 'snag', label: 'Snag photo', sub: 'Camera → AI', show: allowed('chat'), icon: <><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></>, onClick: () => photoRef.current && photoRef.current.click() },
    { id: 'ask', label: 'Ask / Log', sub: 'Speak to the AI', show: allowed('chat'), icon: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></>, onClick: () => { setHandoff({ mic: true }); go('chat') } },
    { id: 'cl', label: 'Checklist', sub: 'Run a procedure', show: allowed('checklists'), icon: <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V3h6v1M9 12l2 2 4-4" /></>, onClick: () => go('checklists') },
    { id: 'ho', label: 'Handover', sub: 'Write my handover', show: allowed('chat') && allowed('rotations'), icon: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>, onClick: () => { setHandoff({ ask: 'Write my handover' }); go('chat') } },
    { id: 'hr', label: 'My hours', sub: 'Hours of rest', show: allowed('hours_rest'), icon: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>, onClick: () => go('hours_rest') },
  ].filter(q => q.show)

  const tone = { r: 'var(--red, #c0434a)', a: '#8a5c0a' }
  return (
    <div className="hc-today" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 640, margin: '0 auto' }}>
      <ApprovalsCard palette={{ card: card.background, line: 'var(--line)', text: 'var(--text)', muted: 'var(--muted)', accent: 'var(--gold2)', red: 'var(--red, #c0434a)', amber: '#7a5108' }} scopeVesselId={config?.id} hideWhenEmpty onOpen={r => go(rememberOpen(r.kind, r.open_id, r.title))} />
      <input ref={photoRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onPhoto} />

      <VesselJobsCard config={config} crew={!config?._companyView && config?.accountType !== 'company'} onOpen={() => go('work_orders')} />{/* Service package: nothing without it */}
      <VesselStatusTiles tiles={tiles} onPick={t => go(t.page, t.filter)} compact />
      <GettingStarted />

      <section style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Briefing</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>{dateLabel}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          <span style={chip}>{place ? (inPort ? '⚓ ' : '→ ') + place.port + (place.country ? ', ' + place.country : '') : '⚓ No port call'}</span>
          {wx && wx.windKn != null && <span style={chip}>{'💨 ' + (wx.windCompass || '') + ' ' + Math.round(wx.windKn) + ' kn' + (wx.gustKn != null ? ', gusts ' + Math.round(wx.gustKn) : '')}</span>}
          {wx && wx.waveM != null && <span style={chip}>{'🌊 Sea ' + Number(wx.waveM).toFixed(1) + ' m' + (wx.wavePeriodS ? ' / ' + Math.round(wx.wavePeriodS) + ' s' : '')}</span>}
        </div>
        <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>TODAY</div>
        {!d ? <div style={muted}>Loading…</div> : top.length === 0 ? <div style={muted}>Nothing scheduled for today.</div> : (
          <ol style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
            {top.slice(0, 3).map((t, i) => (
              <li key={i} onClick={() => go(t.page)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: i ? '1px solid var(--line)' : 'none', cursor: 'pointer' }}>
                <span style={{ width: 22, height: 22, borderRadius: 11, background: 'rgba(77,132,255,.14)', color: '#2457d6', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                <span style={{ fontSize: 11, color: t.tag === 'overdue' ? tone.r : 'var(--muted)', flexShrink: 0 }}>{t.tag}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {QUICK.map(q => (
          <button key={q.id} className="hc-today-btn" onClick={q.onClick} style={{ ...card, padding: '16px 14px', minHeight: 96, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2457d6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{q.icon}</svg>
            <span style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text)' }}>{q.label}</span>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{q.sub}</span>
          </button>
        ))}
      </section>

      <section style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Alerts{d && alerts.length ? ' · ' + alerts.length : ''}</div>
          {alerts.length > 6 && <button onClick={() => go('dashboard_full')} style={link}>See all</button>}
        </div>
        {!d ? <div style={muted}>Loading…</div> : alerts.length === 0 ? <div style={{ ...muted, color: 'var(--green, #137050)' }}>{'✓ Nothing needs attention'}</div> : (
          <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
            {alerts.slice(0, 6).map((a, i) => (
              <li key={i} onClick={() => go(a.page)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: i ? '1px solid var(--line)' : 'none', cursor: 'pointer' }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, background: tone[a.tone], flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: tone[a.tone] }}>{a.what}</span>
                </span>
                <span style={{ color: 'var(--muted)', fontSize: 18 }}>{'›'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <button onClick={() => go('dashboard_full')} style={{ ...link, alignSelf: 'center', padding: '8px 16px', fontSize: 14 }}>{'Full dashboard →'}</button>
    </div>
  )
}

const card = { background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 14, padding: 16 }
const chip = { fontSize: 13, color: 'var(--text)', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 20, padding: '5px 11px' }
const muted = { fontSize: 13.5, color: 'var(--muted)', padding: '8px 0' }
const link = { background: 'none', border: 'none', color: '#2457d6', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }
